import React, { useState, useEffect } from 'react';
import { 
  FileText, Users, Plus, Eye, UserPlus, Trash2, 
  AlertCircle, X, Check, Search, Filter, 
  PlayCircle, StopCircle, Building, Calendar, User
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

export default function Licitaciones() {
  // Estados principales
  const [licitaciones, setLicitaciones] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    estado: ''
  });

  // Estados para modales
  const [showNewModal, setShowNewModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedLicitacion, setSelectedLicitacion] = useState(null);

  // Estados para formularios
  const [formData, setFormData] = useState({
    titulo: '',
    centro: ''
  });
  const [inviteData, setInviteData] = useState({
    proveedor_id: ''
  });

  // Estado para permisos del usuario
  const [userPermissions, setUserPermissions] = useState(null);

  // Cargar licitaciones
  const loadLicitaciones = async () => {
  try {
    setLoading(true);
    const token = localStorage.getItem('token');
    
    if (!token) {
      showMessage('No hay token de autenticación', 'danger');
      return;
    }
    
    const params = new URLSearchParams({
      page: currentPage,
      limit: 10,
      search: filters.search,
      estado: filters.estado
    });

    const response = await fetch(`${API_URL}/api/licitaciones?${params}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (response.ok) {
      const data = await response.json();
      setLicitaciones(data.licitaciones);
      setTotalPages(data.totalPages);
    } else {
      // Manejar diferentes tipos de error
      const errorData = await response.json().catch(() => ({}));
      
      if (response.status === 400) {
        // Error de validación específico para proveedores
        showMessage(errorData.message || 'Error de configuración de usuario', 'danger');
      } else if (response.status === 401) {
        // Token inválido
        showMessage('Sesión expirada. Por favor, inicie sesión nuevamente', 'danger');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else if (response.status === 500) {
        // Error del servidor
        showMessage('Error del servidor. Contacte al administrador.', 'danger');
        console.error('Server error details:', errorData);
      } else {
        showMessage('Error al cargar las licitaciones', 'danger');
      }
    }
  } catch (error) {
    console.error('Error al cargar licitaciones:', error);
    showMessage('Error de conexión al servidor', 'danger');
  } finally {
    setLoading(false);
  }
};

// Obtener permisos del usuario del token o localStorage
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Decodificar el token para obtener los permisos
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserPermissions({
          permisos: payload.permisos || 1,
          proveedor_asociado: payload.proveedor_asociado || null,
          username: payload.username || 'Usuario'
        });
      } catch (error) {
        console.error('Error al decodificar token:', error);
        // Fallback - asumir permisos de admin si no se puede decodificar
        setUserPermissions({ permisos: 1, proveedor_asociado: null, username: 'Admin' });
      }
    } else {
      // Fallback si no hay token
      setUserPermissions({ permisos: 1, proveedor_asociado: null, username: 'Admin' });
    }
  }, []);

  // Cargar datos iniciales
  useEffect(() => {
    if (userPermissions) {
      loadLicitaciones();
      if (userPermissions.permisos === 1) {
        loadProveedores();
      }
    }
  }, [currentPage, filters, userPermissions]);

// Función mejorada para obtener permisos del usuario
useEffect(() => {
  const token = localStorage.getItem('token');
  if (token) {
    try {
      // Decodificar el token para obtener los permisos
      const payload = JSON.parse(atob(token.split('.')[1]));
            
      setUserPermissions({
        permisos: payload.permisos || 1,
        proveedor_asociado: payload.proveedor_asociado || null,
        username: payload.username || 'Usuario'
      });
    } catch (error) {
      console.error('Error al decodificar token:', error);
      showMessage('Token de autenticación inválido', 'danger');
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
  } else {
    showMessage('No hay token de autenticación', 'danger');
    window.location.href = '/login';
  }
}, []);

  // Cargar proveedores (solo para admin)
  const loadProveedores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/proveedores/search`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProveedores(data.proveedores || []);
      }
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    }
  };

  // Crear nueva licitación (solo admin)
  const handleCreateLicitacion = async () => {
    if (userPermissions?.permisos !== 1) {
      showMessage('No tiene permisos para crear licitaciones', 'danger');
      return;
    }
    
    if (!formData.titulo.trim() || !formData.centro.trim()) {
      showMessage('Todos los campos son obligatorios', 'danger');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/api/licitaciones`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        showMessage('Licitación creada correctamente', 'success');
        setShowNewModal(false);
        setFormData({ titulo: '', centro: '' });
        loadLicitaciones();
      } else {
        const errorData = await response.json();
        showMessage(errorData.message || 'Error al crear la licitación', 'danger');
      }
    } catch (error) {
      console.error('Error al crear licitación:', error);
      showMessage('Error al crear la licitación', 'danger');
    } finally {
      setSaving(false);
    }
  };

  // Invitar proveedor (solo admin)
  const handleInviteProveedor = async () => {
    if (userPermissions?.permisos !== 1) {
      showMessage('No tiene permisos para invitar proveedores', 'danger');
      return;
    }
    
    if (!inviteData.proveedor_id) {
      showMessage('Debe seleccionar un proveedor', 'danger');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/api/licitaciones/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          licitacion_id: selectedLicitacion.id,
          proveedor_id: inviteData.proveedor_id
        })
      });

      if (response.ok) {
        showMessage('Proveedor invitado correctamente', 'success');
        setShowInviteModal(false);
        setInviteData({ proveedor_id: '' });
      } else {
        const errorData = await response.json();
        showMessage(errorData.message || 'Error al invitar proveedor', 'danger');
      }
    } catch (error) {
      console.error('Error al invitar proveedor:', error);
      showMessage('Error al invitar proveedor', 'danger');
    } finally {
      setSaving(false);
    }
  };

  // Cambiar estado de licitación (solo admin)
  const handleChangeEstado = async (licitacionId, nuevoEstado) => {
    if (userPermissions?.permisos !== 1) {
      showMessage('No tiene permisos para cambiar estado de licitaciones', 'danger');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/licitaciones/${licitacionId}/estado`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ estado: nuevoEstado })
      });

      if (response.ok) {
        showMessage(`Licitación ${nuevoEstado === 1 ? 'activada' : 'cerrada'} correctamente`, 'success');
        loadLicitaciones();
      } else {
        const errorData = await response.json();
        showMessage(errorData.message || 'Error al cambiar estado', 'danger');
      }
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      showMessage('Error al cambiar estado', 'danger');
    }
  };

  // Eliminar licitación (solo admin)
  const handleDelete = async (licitacionId) => {
    if (userPermissions?.permisos !== 1) {
      showMessage('No tiene permisos para eliminar licitaciones', 'danger');
      return;
    }

    if (!window.confirm('¿Está seguro de eliminar esta licitación?')) return;

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/licitaciones/${licitacionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        showMessage('Licitación eliminada correctamente', 'success');
        loadLicitaciones();
      } else {
        const errorData = await response.json();
        showMessage(errorData.message || 'Error al eliminar licitación', 'danger');
      }
    } catch (error) {
      console.error('Error al eliminar licitación:', error);
      showMessage('Error al eliminar licitación', 'danger');
    }
  };

  // Ver productos de licitación
  const handleViewProducts = (licitacionId) => {
    window.location.href = `/licitaciones/${licitacionId}/productos`;
  };

  // Manejar búsqueda
  const handleSearch = () => {
    setCurrentPage(1);
    loadLicitaciones();
  };

  // Resetear filtros
  const handleResetFilters = () => {
    setFilters({ search: '', estado: '' });
    setCurrentPage(1);
  };

  // Manejar Enter en búsqueda
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Utilidades
  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 8080);
  };

  const getEstadoBadge = (estado) => {
    if (estado === 1) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <PlayCircle className="w-3 h-3 mr-1" />
          Activa
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <StopCircle className="w-3 h-3 mr-1" />
          Cerrada
        </span>
      );
    }
  };

  const getUserRoleBadge = () => {
    if (!userPermissions) return null;
    
    switch (userPermissions.permisos) {
      case 1:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <User className="w-3 h-3 mr-1" />
            Administrador
          </span>
        );
      case 4:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            <User className="w-3 h-3 mr-1" />
            Proveedor
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <User className="w-3 h-3 mr-1" />
            Usuario
          </span>
        );
    }
  };

  // Mostrar loading si no tenemos permisos aún
  if (!userPermissions) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="shadow-lg border-b-2 bg-white" style={{borderBottomColor: '#4DD5AF'}}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg" style={{backgroundColor: '#4DD5AF'}}>
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Gestión de Licitaciones</h1>
                <p className="text-sm font-medium" style={{color: '#4DD5AF'}}>
                  {userPermissions.permisos === 4 ? 'Licitaciones asignadas' : 'Administrar licitaciones y proveedores'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {getUserRoleBadge()}
              <span className="text-sm text-gray-600">
                {userPermissions.username}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Info Bar */}
      <div className="text-white" style={{background: `linear-gradient(to right, #4DD5AF, #34d399, #10b981)`}}>
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center space-x-2">
            <Users className="w-4 h-4" />
            <span className="font-bold">
              {userPermissions.permisos === 4 ? 'Panel de Proveedor' : 'Sistema de Licitaciones'}
            </span>
          </div>
        </div>
      </div>

      {/* Mensaje de estado */}
      {message.text && (
        <div className="max-w-7xl mx-auto px-6 pt-4">
          <div className={`p-3 rounded-lg flex items-center shadow-lg border text-sm ${
            message.type === 'success' ? 'border-emerald-200' : 'border-red-200'
          }`}
          style={{
            background: message.type === 'success' ? 'linear-gradient(135deg, #ecfdf5, #d1fae5)' : 'linear-gradient(135deg, #fef2f2, #fecaca)',
            color: message.type === 'success' ? '#065f46' : '#991b1b'
          }}>
            {message.type === 'success' ? (
              <Check className="w-4 h-4 mr-2 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            )}
            <span className="font-medium flex-1">{message.text}</span>
            <button 
              onClick={() => setMessage({ text: '', type: '' })}
              className="ml-auto p-1 hover:bg-white hover:bg-opacity-30 rounded"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Contenido principal */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Barra de herramientas */}
        <div className="bg-white rounded-xl shadow-lg border-2 p-6 mb-6" style={{borderColor: '#4DD5AF'}}>
          <div className="flex flex-col gap-4">
            {/* Fila de filtros - Horizontal */}
            <div className="flex flex-wrap items-center gap-4">
              {/* Búsqueda */}
              <div className="flex-1 min-w-64">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Buscar licitaciones..."
                    className="w-full pl-10 pr-4 py-2 border-2 rounded-lg focus:outline-none focus:border-green-500"
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    onKeyPress={handleSearchKeyPress}
                  />
                </div>
              </div>
              
              {/* Filtro de estado */}
              <div className="flex items-center gap-2">
                <Filter className="text-gray-400 w-4 h-4" />
                <select
                  className="px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-green-500 min-w-40"
                  value={filters.estado}
                  onChange={(e) => setFilters(prev => ({ ...prev, estado: e.target.value }))}
                >
                  <option value="">Todos los estados</option>
                  <option value="1">Activas</option>
                  <option value="0">Cerradas</option>
                </select>
              </div>
              
              {/* Botones de acción */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSearch}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  Buscar
                </button>
                
                <button
                  onClick={handleResetFilters}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Limpiar
                </button>
              </div>
            </div>

            {/* Botón nueva licitación - Solo para admin */}
            {userPermissions.permisos === 1 && (
              <div className="flex justify-between items-center border-t pt-4">
                <span className="text-sm text-gray-600">
                  Total: {licitaciones.length} licitaciones
                </span>
                <button
                  onClick={() => setShowNewModal(true)}
                  className="px-4 py-2 text-white rounded-lg hover:opacity-90 flex items-center gap-2"
                  style={{backgroundColor: '#4DD5AF'}}
                >
                  <Plus className="w-4 h-4" />
                  Nueva Licitación
                </button>
              </div>
            )}
            
            {/* Información para proveedor */}
            {userPermissions.permisos === 4 && (
              <div className="border-t pt-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Información:</strong> Solo puede ver las licitaciones en las que ha sido invitado a participar.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tabla de licitaciones */}
        <div className="bg-white rounded-xl shadow-lg border-2" style={{borderColor: '#4DD5AF'}}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="text-white" style={{backgroundColor: '#4DD5AF'}}>
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">Título</th>
                  {userPermissions.permisos === 1 && (
                    <th className="px-6 py-4 text-left font-semibold">Centro</th>
                  )}
                  <th className="px-6 py-4 text-center font-semibold">Estado</th>
                  <th className="px-6 py-4 text-center font-semibold">Fecha Creación</th>
                  <th className="px-6 py-4 text-center font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={userPermissions.permisos === 1 ? "5" : "4"} className="px-6 py-8 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-gray-600">Cargando licitaciones...</span>
                      </div>
                    </td>
                  </tr>
                ) : licitaciones.length === 0 ? (
                  <tr>
                    <td colSpan={userPermissions.permisos === 1 ? "5" : "4"} className="px-6 py-8 text-center text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg font-medium">
                        {userPermissions.permisos === 4 ? 'No tiene licitaciones asignadas' : 'No hay licitaciones'}
                      </p>
                      <p className="text-sm">
                        {userPermissions.permisos === 4 
                          ? 'Contacte con el administrador si necesita acceso a alguna licitación'
                          : 'Crea tu primera licitación para comenzar'
                        }
                      </p>
                    </td>
                  </tr>
                ) : (
                  licitaciones.map((licitacion) => (
                    <tr key={licitacion.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{licitacion.titulo}</div>
                      </td>
                      {userPermissions.permisos === 1 && (
                        <td className="px-6 py-4 text-gray-600">
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-gray-400" />
                            {licitacion.centro}
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 text-center">
                        {getEstadoBadge(licitacion.estado)}
                      </td>
                      <td className="px-6 py-4 text-center text-gray-600">
                        <div className="flex items-center justify-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {new Date(licitacion.fecha_creacion).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          {/* Ver productos - Disponible para todos */}
                          <button
                            onClick={() => handleViewProducts(licitacion.id)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                            title="Ver productos"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          
                          {/* Acciones solo para admin */}
                          {userPermissions.permisos === 1 && (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedLicitacion(licitacion);
                                  setShowInviteModal(true);
                                }}
                                className="p-2 text-green-600 hover:bg-green-100 rounded-lg"
                                title="Invitar proveedor"
                              >
                                <UserPlus className="w-4 h-4" />
                              </button>
                              
                              <button
                                onClick={() => handleChangeEstado(licitacion.id, licitacion.estado === 1 ? 0 : 1)}
                                className={`p-2 rounded-lg ${licitacion.estado === 1 ? 'text-orange-600 hover:bg-orange-100' : 'text-green-600 hover:bg-green-100'}`}
                                title={licitacion.estado === 1 ? 'Cerrar licitación' : 'Reabrir licitación'}
                              >
                                {licitacion.estado === 1 ? <StopCircle className="w-4 h-4" /> : <PlayCircle className="w-4 h-4" />}
                              </button>
                              
                              <button
                                onClick={() => handleDelete(licitacion.id)}
                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                                title="Eliminar licitación"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Página {currentPage} de {totalPages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Nueva Licitación - Solo admin */}
      {userPermissions.permisos === 1 && showNewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Nueva Licitación</h3>
                <button
                  onClick={() => setShowNewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Título *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-green-500"
                    value={formData.titulo}
                    onChange={(e) => setFormData(prev => ({ ...prev, titulo: e.target.value }))}
                    placeholder="Título de la licitación"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Centro *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-green-500"
                    value={formData.centro}
                    onChange={(e) => setFormData(prev => ({ ...prev, centro: e.target.value }))}
                    placeholder="Centro o ubicación"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowNewModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleCreateLicitacion}
                  disabled={saving}
                  className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                  style={{backgroundColor: '#4DD5AF'}}
                >
                  {saving ? 'Creando...' : 'Crear Licitación'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Invitar Proveedor - Solo admin */}
      {userPermissions.permisos === 1 && showInviteModal && selectedLicitacion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Invitar Proveedor</h3>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Licitación
                  </label>
                  <input
                    type="text"
                    readOnly
                    className="w-full px-3 py-2 border-2 rounded-lg bg-gray-50"
                    value={selectedLicitacion.titulo}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Seleccionar Proveedor *
                  </label>
                  <select
                    required
                    className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-green-500"
                    value={inviteData.proveedor_id}
                    onChange={(e) => setInviteData(prev => ({ ...prev, proveedor_id: e.target.value }))}
                  >
                    <option value="">-- Seleccione un proveedor --</option>
                    {proveedores.map(proveedor => (
                      <option key={proveedor.id} value={proveedor.id}>
                        {proveedor.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleInviteProveedor}
                  disabled={saving}
                  className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                  style={{backgroundColor: '#4DD5AF'}}
                >
                  {saving ? 'Invitando...' : 'Invitar Proveedor'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}